/**
 * Module-level cache of deactivated (inactive/closed) Confluence authors mined from the live
 * search results.
 *
 * Why this exists:
 * - The Contributor filter's type-ahead lookup is powered by URS, which never returns deactivated
 *   users (they are removed from the CPUS index). So typing a deactivated user's name yields
 *   nothing from the server.
 * - The search results (Relay) DO expose `author` with `accountStatus` on Confluence entities. The
 *   result renderer records any inactive/closed author here as it renders, and the Contributor
 *   lookup reads this cache to surface matching deactivated users alongside the URS results.
 *   `author` (the original creator) is used rather than `owner` because the creator is always a
 *   contributor to the page, so selecting them in the filter actually returns that result;
 *   ownership can be transferred to someone who never edited the page.
 *
 * Only `accountStatus !== 'active'` users are stored, so active users (already covered by URS
 * recommendations) are never duplicated into the filter.
 */

export type MinedDeactivatedUser = {
	accountId: string;
	name: string;
	picture?: string | null;
	accountStatus: 'inactive' | 'closed';
};

type Listener = () => void;

type MaybeUser =
	| {
			readonly accountId?: string | null;
			readonly name?: string | null;
			readonly picture?: string | null;
			// Accept the Relay-generated AccountStatus enum (string-literal union incl.
			// "%future added value") as well as plain strings.
			readonly accountStatus?: string | null;
	  }
	| null
	| undefined;

// Keyed by accountId so repeated results (same author across many pages) de-dupe naturally. The
// reference count keeps the cache aligned with the results that are currently mounted.
const cache = new Map<string, { user: MinedDeactivatedUser; references: number }>();
const listeners = new Set<Listener>();
let version = 0;

const notifyListeners = (): void => {
	version += 1;
	listeners.forEach((listener) => listener());
};

export const subscribeToMinedDeactivatedUsers = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

/** Stable primitive snapshot for React's useSyncExternalStore. */
export const getMinedDeactivatedUsersVersion = (): number => version;

const isInactiveOrClosed = (status?: string | null): status is 'inactive' | 'closed' =>
	status === 'inactive' || status === 'closed';

/**
 * Record users mined from a search result. Non-inactive users and users missing an accountId are
 * ignored.
 */
export const recordMinedUsers = (users: ReadonlyArray<MaybeUser>): (() => void) => {
	const recordedAccountIds: string[] = [];
	for (const user of users) {
		if (!user?.accountId || !isInactiveOrClosed(user.accountStatus)) {
			continue;
		}
		const existing = cache.get(user.accountId);
		cache.set(user.accountId, {
			user: {
				accountId: user.accountId,
				name: user.name ?? '',
				picture: user.picture ?? undefined,
				accountStatus: user.accountStatus,
			},
			references: (existing?.references ?? 0) + 1,
		});
		recordedAccountIds.push(user.accountId);
	}

	if (recordedAccountIds.length > 0) {
		notifyListeners();
	}

	return () => {
		let didChange = false;
		for (const accountId of recordedAccountIds) {
			const existing = cache.get(accountId);
			if (!existing) {
				continue;
			}
			if (existing.references === 1) {
				cache.delete(accountId);
			} else {
				cache.set(accountId, { ...existing, references: existing.references - 1 });
			}
			didChange = true;
		}
		if (didChange) {
			notifyListeners();
		}
	};
};

/** Return all mined deactivated users currently cached. */
export const getMinedDeactivatedUsers = (): MinedDeactivatedUser[] =>
	Array.from(cache.values(), ({ user }) => user);

/**
 * Return mined deactivated users whose display name matches the (case-insensitive) query. An empty
 * query returns all cached users.
 */
export const findMinedDeactivatedUsers = (query: string): MinedDeactivatedUser[] => {
	const normalised = query.trim().toLowerCase();
	if (!normalised) {
		return getMinedDeactivatedUsers();
	}
	return getMinedDeactivatedUsers().filter((user) => user.name.toLowerCase().includes(normalised));
};

/** Clear the cache. Intended for tests and (optionally) on new-search reset. */
export const clearMinedDeactivatedUsers = (): void => {
	if (cache.size === 0) {
		return;
	}
	cache.clear();
	notifyListeners();
};
